// Copyright (c) StrangeBee 2023

import { FormSubmitButton } from '@Atoms/Buttons/FormSubmitButton/FormSubmitButton';
import client from '@Client';
import useListQuery from '@Hooks/useListQuery';
import useValidateForm from '@Hooks/useValidateForm';
import { THSelect } from '@Molecules/Inputs/THSelect/THSelect';
import THDrawer from '@Molecules/THDrawer/THDrawer';
import { THForm } from '@Molecules/THForm/THForm';
import { computeListSelector } from '@Organisms/ReportTemplate/selector';
import { useGenericQuery } from '@Queries/genericQueries';
import React, { ReactElement, useState } from 'react';
import { CaseEventList } from '../../eventList';
import { SelectValue } from 'antd/lib/select';
import { THRadio } from '@Molecules/Inputs/THRadio/THRadio';
import { useLocalMessage } from '@Helpers/locale';
import { classList } from '@Helpers/className';
import { useParams } from 'react-router-dom';
import { Spin } from 'antd';
import { CaseReportPreview } from '../CaseReportPreview/CaseReportPreview';
import { ICONS } from '@Constants/iconsApp';
import {
    CaseReportAttachmentOutput,
    CaseReportCreateInput,
    ReportTemplateOutput,
    CaseReportFormatOutput,
    CaseReportRenderInput,
    CaseReportFormatType,
} from 'thehive-sdk';

import './CaseReportCreate.scss';

interface CaseReportCreateProps {
    eventList: CaseEventList;
    triggerElem: ReactElement;
    onFinish: (data: CaseReportCreateInput) => Promise<CaseReportAttachmentOutput | void>;
}

export const CaseReportCreate = ({ eventList, onFinish, triggerElem }: CaseReportCreateProps) => {
    const i = useLocalMessage();
    const [activeReport, setActiveReport] = useState<ReportTemplateOutput>();
    const [format, setFormat] = useState<CaseReportFormatType>();

    const { caseId } = useParams<{ caseId: string }>();

    const { form, isValid, onFormValuesChange, fieldsTouched, setFieldsTouched, loading, setLoading } =
        useValidateForm();

    const { data: dataFormats } = useGenericQuery<CaseReportFormatOutput>({
        fetchCallback: () => eventList.onListReportFormat(),
        key: ['get-case-report-formats'],
    });

    const formatList = dataFormats?.formats || [];

    const { data: reportTemplateList = [] } = useListQuery<ReportTemplateOutput[]>({
        controller: client.ReportTemplateController,
        queryParams: {
            selector: computeListSelector(),
            sort: ['+title'],
        },
    });

    const { data: reportRender, isLoading } = useGenericQuery<string>({
        fetchCallback: () => {
            if (!activeReport || !format || format === 'word') {
                return Promise.reject();
            }
            const { definition, _id } = activeReport;

            const data: CaseReportRenderInput = {
                format,
                definition,
                caseId,
                caseReportTemplateId: _id,
                maxElements: 3,
            };

            return eventList.onRenderReport(data);
        },
        fetchOnStart: !!activeReport && !!format && format !== 'word',
        key: ['get-case-report-render', format, activeReport],
    });

    const handleChange = (id: SelectValue) => {
        const reportTemplate = reportTemplateList.find(({ _id }) => _id === id);

        setActiveReport(reportTemplate);
    };

    const resetForm = () => {
        form.resetFields();
        setFieldsTouched(false);
        setActiveReport(undefined);
        setFormat(undefined);
    };

    return (
        <THDrawer
            title={i('report.create.drawer-title')}
            triggerElem={triggerElem}
            className="drawer-form"
            disableClose={fieldsTouched}
            afterVisibleChange={() => {
                form.resetFields();
                setFieldsTouched(false);
            }}
        >
            {(toggleDrawer) => (
                <THForm
                    className="case-report-create"
                    form={form}
                    onValuesChange={() => {
                        onFormValuesChange();
                        setFieldsTouched(true);
                    }}
                    onFinish={(values) => {
                        setLoading(true);
                        onFinish(values)
                            .then(() => {
                                toggleDrawer();
                                setFieldsTouched(true);
                                resetForm();
                                setLoading(false);
                            })
                            .finally(() => setLoading(false));
                    }}
                >
                    <THForm.Layout
                        footer={
                            <FormSubmitButton
                                footerText={i('report.create.footer')}
                                disabled={!isValid || loading}
                                loading={loading}
                                onCancel={() => {
                                    toggleDrawer();
                                    resetForm();
                                }}
                            />
                        }
                    >
                        <THForm.Item
                            name="caseReportTemplateId"
                            label={i('report.create.form.report-template.label')}
                            className="case-report-create__item-report"
                            rules={[{ required: true }]}
                        >
                            <THSelect
                                placeholder={i('report.create.form.report-template.placeholder')}
                                options={reportTemplateList.map(({ title, _id }) => ({ label: title, value: _id }))}
                                onChange={handleChange}
                                showSearch
                                filterOption={(inputValue, option) => {
                                    const label = (option?.label as string) || '';

                                    return label?.toLowerCase().includes(inputValue?.toLowerCase());
                                }}
                            />
                        </THForm.Item>

                        {activeReport?.description && (
                            <THForm.Item
                                name="description"
                                label={i('report.create.form.description.label')}
                                className="case-report-create__item-description"
                            >
                                <p className="case-report-create__description">{activeReport.description}</p>
                            </THForm.Item>
                        )}

                        {activeReport && (
                            <THForm.Item
                                name="format"
                                label={i('report.create.form.format.label')}
                                rules={[{ required: true }]}
                                className="case-report-create__item-format"
                            >
                                <THRadio.Group buttonStyle="solid">
                                    {formatList.map((format) => (
                                        <THRadio.Button
                                            key={format}
                                            value={format}
                                            onChange={() => setFormat(format)}
                                            className={classList('case-report-create__format', {
                                                'case-report-create__format--uppercase': format === 'html',
                                            })}
                                        >
                                            {format}
                                        </THRadio.Button>
                                    ))}
                                </THRadio.Group>
                            </THForm.Item>
                        )}

                        {activeReport && reportRender && format && format !== 'word' && (
                            <CaseReportPreview
                                reportRender={reportRender}
                                format={format}
                                caseId={caseId}
                                reportTemplateId={activeReport._id}
                            />
                        )}

                        {isLoading && <Spin />}

                        {reportRender && (
                            <div className="case-report-create__tooltip">
                                <ICONS.InfoCircleOutlined /> {i('report.create.form.preview.info')}
                            </div>
                        )}

                        {activeReport && format && format === 'word' && (
                            <div className="case-report-create__tooltip">
                                <ICONS.InfoCircleOutlined /> {i('report.create.form.preview.no-word-preview')}
                            </div>
                        )}
                    </THForm.Layout>
                </THForm>
            )}
        </THDrawer>
    );
};
